
import { Link } from 'react-router-dom';

export default function CoreConceptsPage() {
  const concepts = [
    {
      title: 'チーム成果は「個人能力の総和」ではなく相互作用の質で決まる',
      description: '優秀な個人を集めても、相互作用が機能しなければチームとしての成果は出ない。逆に、個々の能力が平均的でも、相互作用の質が高ければ高い成果を生み出せる。',
      icon: 'ri-team-line',
      examples: [
        '情報共有がスムーズに行われる',
        '役割分担が明確で重複や漏れがない',
        '互いの強みを活かし合える',
        '問題が早期に共有され解決される'
      ]
    },
    {
      title: 'チームビルディング＝人間関係改善ではなく、構造設計',
      description: '「仲良くなれば成果が出る」という誤解が多いが、チームビルディングの本質は構造設計。役割・目標・プロセスを明確にし、相互作用が自然に生まれる仕組みをつくること。',
      icon: 'ri-settings-3-line',
      examples: [
        '役割と責任範囲の明確化',
        '意思決定プロセスの可視化',
        '情報共有の仕組み化',
        '振り返りの定期実施'
      ]
    },
    {
      title: '目的：チームが継続的に成果を出せる状態をつくること',
      description: '一時的な成果ではなく、継続的に成果を出せる状態を目指す。そのためには、学習し改善し続けられる仕組みが必要。',
      icon: 'ri-refresh-line',
      examples: [
        '定期的な振り返りの実施',
        '改善提案が出やすい環境',
        '失敗から学ぶ文化',
        'プロセスの継続的改善'
      ]
    }
  ];

  const pharmacyApplications = [
    {
      title: '朝礼・ミーティングの再設計',
      description: '連絡事項の共有だけでなく、役割確認・優先順位の共有・問題の早期発見の場として機能させる',
      icon: 'ri-calendar-check-line'
    },
    {
      title: '在宅・地域連携の構造化',
      description: '誰が何をどこまで担うか、情報共有のルール、判断基準を明確にする',
      icon: 'ri-home-heart-line'
    },
    {
      title: '属人業務の可視化と分散',
      description: '特定の人しかできない業務を洗い出し、手順化・共有化する',
      icon: 'ri-file-list-3-line'
    },
    {
      title: 'ヒヤリハット共有の仕組み化',
      description: '個人の責任追及ではなく、システム改善の機会として扱う文化をつくる',
      icon: 'ri-alert-line'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-rose-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-rose-600 transition-colors duration-200">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/team-building" className="hover:text-rose-600 transition-colors duration-200">チームビルディング</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-rose-600 font-medium">中核概念</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl mb-6 shadow-lg">
              <i className="ri-lightbulb-line text-3xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              Ⅰ．チームビルディングの中核概念
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              チームビルディングの本質を理解し、薬局運営に活かす
            </p>
          </div>
        </div>
      </section>

      {/* Core Concepts */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-8">
            {concepts.map((concept, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-rose-100">
                <div className="flex items-start gap-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl shadow-md flex-shrink-0">
                    <i className={`${concept.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{concept.title}</h2>
                    <p className="text-gray-700 leading-relaxed mb-6">{concept.description}</p>
                    <div className="bg-rose-50 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-rose-900 mb-4 flex items-center gap-2">
                        <i className="ri-checkbox-circle-line"></i>
                        具体例
                      </h3>
                      <ul className="space-y-3">
                        {concept.examples.map((example, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-gray-700">
                            <i className="ri-arrow-right-s-line text-rose-600 mt-1 flex-shrink-0"></i>
                            <span>{example}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pharmacy Applications */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">薬局運営への応用</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {pharmacyApplications.map((app, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-rose-100 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg mb-4">
                    <i className={`${app.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{app.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{app.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Action Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-8 text-white shadow-xl">
              <div className="flex items-start gap-6">
                <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-4">明日の一手</h2>
                  <p className="text-lg leading-relaxed mb-6">
                    次回のミーティングで「今のチームで一番うまくいっていることは何か？」「それはなぜうまくいっているのか？」を全員で話し合ってみましょう。成功の構造を理解することが、チームビルディングの第一歩です。
                  </p>
                  <div className="flex items-center gap-2 text-sm bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg inline-flex whitespace-nowrap">
                    <i className="ri-time-line"></i>
                    <span>所要時間：15〜20分</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center">
              <Link
                to="/team-building"
                className="flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors duration-200"
              >
                <i className="ri-arrow-left-line"></i>
                <span>一覧に戻る</span>
              </Link>
              <Link
                to="/team-building/four-elements"
                className="flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                <span>次へ：四大要素</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
